import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { fmtDate, fmtUsd } from '@/lib/format';
import type { BudgetRequestDetail, Caller } from '@/types';

export function Approvals() {
  const api = useApi();
  const pending = useQuery({
    queryKey: ['admin-callers', 'Pending'],
    queryFn: () => api.adminListCallers('Pending')
  });
  const budgetRequests = useQuery({
    queryKey: ['admin-budget-requests', 'Pending'],
    queryFn: () => api.adminListBudgetRequests('Pending')
  });

  if (pending.isLoading) return <div className="placeholder">Loading pending requests...</div>;
  if (pending.error) return <div className="error-banner">Could not load requests: {(pending.error as Error).message}</div>;

  const rows = pending.data ?? [];
  const budgetRows = budgetRequests.data ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Approvals</h1>
          <div className="sub">
            {rows.length} pending access {rows.length === 1 ? 'request' : 'requests'}
            {budgetRows.length > 0 && ` · ${budgetRows.length} budget ${budgetRows.length === 1 ? 'request' : 'requests'}`}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 22 }}>
        <div className="card-header"><h3>Access requests</h3></div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Justification</th>
              <th>Requested</th>
              <th>Monthly budget (USD)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="placeholder">No pending requests — everyone's been looked after.</td></tr>
            ) : rows.map(c => <ApprovalRow key={c.id} caller={c} />)}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="card-header"><h3>Budget increase requests</h3></div>
        {budgetRequests.error && (
          <div className="card-body">
            <div className="error-banner">Could not load budget requests: {(budgetRequests.error as Error).message}</div>
          </div>
        )}
        <table className="data-table">
          <thead>
            <tr>
              <th>Caller</th>
              <th>Current</th>
              <th>Spend when asked</th>
              <th>Requested</th>
              <th>Justification</th>
              <th>Asked</th>
              <th>Grant (USD)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {budgetRows.length === 0 ? (
              <tr><td colSpan={8} className="placeholder">No pending budget requests.</td></tr>
            ) : budgetRows.map(r => <BudgetRequestRow key={r.id} request={r} />)}
          </tbody>
        </table>
      </div>
    </>
  );
}

function BudgetRequestRow({ request }: { request: BudgetRequestDetail }) {
  const api = useApi();
  const qc = useQueryClient();
  // Pre-fill with what they asked for; the admin can override before granting.
  const [amount, setAmount] = useState(request.requestedBudgetUsd === null ? '' : String(request.requestedBudgetUsd));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-budget-requests'] });
    qc.invalidateQueries({ queryKey: ['admin-callers'] });
  };

  const approve = useMutation({
    mutationFn: () => {
      const trimmed = amount.trim();
      return api.adminApproveBudgetRequest(request.id,
        trimmed === '' ? { makeUnlimited: true } : { approvedBudgetUsd: Number(trimmed) });
    },
    onSuccess: invalidate
  });

  const reject = useMutation({
    mutationFn: () => api.adminRejectBudgetRequest(request.id),
    onSuccess: invalidate
  });

  const amountInvalid = amount.trim() !== '' && (Number.isNaN(Number(amount)) || Number(amount) <= 0);
  const busy = approve.isPending || reject.isPending;
  const error = (approve.error ?? reject.error) as Error | null;

  return (
    <tr>
      <td>
        <div className="row-title">{request.callerDisplayName}</div>
        <div className="row-sub">{request.callerEmail ?? request.callerType}</div>
        {!request.notificationSent && (
          <div style={{ fontSize: '.72rem', color: 'var(--kt-orange)' }} title="The notification email could not be sent — this request only appeared here.">
            email not delivered
          </div>
        )}
      </td>
      <td>{request.currentBudgetUsd === null ? 'Unlimited' : fmtUsd(request.currentBudgetUsd)}</td>
      <td>{fmtUsd(request.spendAtRequestUsd)}</td>
      <td>{request.requestedBudgetUsd === null ? '—' : fmtUsd(request.requestedBudgetUsd)}</td>
      <td style={{ maxWidth: 280 }}>{request.justification ?? <span style={{ color: 'var(--kt-grey-400)' }}>—</span>}</td>
      <td>{fmtDate(request.requestedAtUtc)}</td>
      <td>
        <input
          className="cell-input"
          type="number"
          min={1}
          step={1}
          placeholder="Unlimited"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          aria-label={`Budget to grant ${request.callerDisplayName}`}
          style={{ borderColor: amountInvalid ? 'var(--kt-red)' : undefined }}
        />
        <div style={{ fontSize: '.72rem', color: 'var(--kt-grey-400)', marginTop: 2 }}>Empty = unlimited</div>
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {error && <div style={{ color: 'var(--kt-red)', fontSize: '.78rem', marginBottom: 4 }}>{error.message}</div>}
        <button className="btn-primary btn-sm" disabled={busy || amountInvalid} onClick={() => approve.mutate()}>
          {approve.isPending ? 'Approving...' : 'Approve'}
        </button>{' '}
        <button className="btn-danger btn-sm" disabled={busy} onClick={() => reject.mutate()}>
          Reject
        </button>
      </td>
    </tr>
  );
}

function ApprovalRow({ caller }: { caller: Caller }) {
  const api = useApi();
  const qc = useQueryClient();
  const [budget, setBudget] = useState('');

  const approve = useMutation({
    mutationFn: () => {
      const trimmed = budget.trim();
      const value = trimmed === '' ? null : Number(trimmed);
      return api.adminApproveCaller(caller.id, value);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-callers'] })
  });

  const budgetInvalid = budget.trim() !== '' && (Number.isNaN(Number(budget)) || Number(budget) <= 0);

  return (
    <tr>
      <td><span className="row-title">{caller.displayName}</span></td>
      <td>{caller.email ?? '—'}</td>
      <td style={{ maxWidth: 340 }}>{caller.accessJustification ?? <span style={{ color: 'var(--kt-grey-400)' }}>None given</span>}</td>
      <td>{fmtDate(caller.createdAtUtc)}</td>
      <td>
        <input
          className="cell-input"
          type="number"
          min={0}
          step={1}
          placeholder="Unlimited"
          value={budget}
          onChange={e => setBudget(e.target.value)}
          aria-label={`Monthly budget for ${caller.displayName}`}
        />
      </td>
      <td style={{ textAlign: 'right' }}>
        {approve.error && (
          <div style={{ color: 'var(--kt-red)', fontSize: '.8rem', marginBottom: 4 }}>
            {(approve.error as Error).message}
          </div>
        )}
        <button
          className="btn-primary btn-sm"
          onClick={() => approve.mutate()}
          disabled={approve.isPending || budgetInvalid}>
          {approve.isPending ? 'Approving...' : 'Approve'}
        </button>
      </td>
    </tr>
  );
}
